/**
 * Leo Palilo Cart Drawer — fetch-based cart operations, no page reloads.
 * Listens for 'cart:open' custom event to slide open.
 * Dispatches 'cart:updated' after every change so other components can react.
 */
(function() {
  'use strict';

  const SELECTORS = {
    drawer:    '.lp-cart-drawer',
    backdrop:  '.lp-cart-drawer__backdrop',
    close:     '[data-cart-close]',
    body:      '.lp-cart-drawer__body',
    itemsWrap: '.lp-cart-drawer__items',
    emptyState:'.lp-cart-drawer__empty',
    filledState:'.lp-cart-drawer__filled',
    footer:    '.lp-cart-drawer__footer',
    subtotal:  '[data-cart-subtotal]',
    itemCount: '[data-cart-count]',
    installments: '[data-cart-installments]',
    headerCount: '.lp-header__cart-count',
    progressBar: '[data-progress-bar]',
    progressFill:'[data-progress-fill]',
    progressMsg: '[data-progress-msg]',
  };

  let isOpen = false;
  let cartData = null;

  /* ── Helpers ── */
  function formatMoney(cents) {
    var amount = (cents / 100);
    var formatted = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
    return formatted + ' ₪';
  }

  function displayProductTitle(handle, fallbackTitle) {
    switch (handle) {
      case 'sitting-pouch':
        return 'פאוץ׳ נשיאה Leo';
      case 'pouch-to-bag':
        return 'רצועת Pouch-To-Bag';
      case 'extension':
        return 'חגורת הארכה למותן';
      default:
        return fallbackTitle;
    }
  }

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return (ctx || document).querySelectorAll(sel); }

  function updateHeaderCartState(count) {
    var headerBubble = $(SELECTORS.headerCount);
    if (headerBubble) {
      headerBubble.textContent = count;
      headerBubble.style.display = count > 0 ? '' : 'none';
    }

    var headerButton = $('#cart-icon-bubble');
    if (headerButton) {
      headerButton.setAttribute('aria-label', 'עגלת קניות (' + count + ')');
      var srText = headerButton.querySelector('.visually-hidden');
      if (srText) srText.textContent = 'סל קניות (' + count + ')';
    }
  }

  /* ── Open / Close ── */
  function open() {
    const drawer = $(SELECTORS.drawer);
    if (!drawer || isOpen) return;
    isOpen = true;
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    fetchAndRender();
  }

  function close() {
    const drawer = $(SELECTORS.drawer);
    if (!drawer || !isOpen) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /* ── Fetch cart state ── */
  async function fetchCart() {
    try {
      const res = await fetch('/cart.json', { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('Cart fetch failed');
      cartData = await res.json();
      return cartData;
    } catch (err) {
      console.error('Cart fetch error:', err);
      return null;
    }
  }

  /* ── Cart API operations ── */
  async function changeItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity }),
      });
      if (!res.ok) throw new Error('Change failed');
      cartData = await res.json();
      render(cartData);
      dispatchUpdated(cartData);
    } catch (err) {
      console.error('Cart change error:', err);
    }
  }

  async function addItem(variantId, quantity) {
    quantity = quantity || 1;
    try {
      const res = await fetch('/cart/add.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: quantity }] }),
      });
      if (!res.ok) throw new Error('Add failed');
      await fetchAndRender();
      dispatchUpdated(cartData);
    } catch (err) {
      console.error('Cart add error:', err);
    }
  }

  /* ── Dispatch event ── */
  function dispatchUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
  }

  /* ── Fetch + render ── */
  async function fetchAndRender() {
    const cart = await fetchCart();
    if (cart) render(cart);
  }

  /* ── Render cart ── */
  function render(cart) {
    const drawer = $(SELECTORS.drawer);
    if (!drawer) return;

    const isEmpty = cart.item_count === 0;
    const emptyEl = $(SELECTORS.emptyState, drawer);
    const filledEl = $(SELECTORS.filledState, drawer);
    const footerEl = $(SELECTORS.footer, drawer);

    if (emptyEl) emptyEl.style.display = isEmpty ? '' : 'none';
    if (filledEl) filledEl.style.display = isEmpty ? 'none' : '';
    if (footerEl) footerEl.style.display = isEmpty ? 'none' : '';

    /* Update item count in drawer header */
    $$(SELECTORS.itemCount).forEach(function(el) {
      el.textContent = cart.item_count;
    });

    /* Update header cart bubble + screen-reader label */
    updateHeaderCartState(cart.item_count);

    /* Update subtotal */
    var subtotalEl = $(SELECTORS.subtotal, drawer);
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    /* Update installments */
    var instEl = $(SELECTORS.installments, drawer);
    if (instEl) {
      instEl.textContent = formatMoney(Math.round(cart.total_price / 4));
      instEl.parentElement.style.display = isEmpty ? 'none' : '';
    }

    /* Render line items — then enrich with compare_at_price from product API */
    var itemsWrap = $(SELECTORS.itemsWrap, drawer);
    if (itemsWrap && !isEmpty) {
      var lockedGiftText = drawer.dataset.lockedGiftText || 'כמות ניתנת עם הפאוץ׳';
      var freeLabelText = drawer.dataset.freeLabelText || 'חינם';
      itemsWrap.innerHTML = cart.items.map(function(item) {
        var imgSrc = item.featured_image ? item.featured_image.url.replace(/\.([^.]+)$/, '_200x200_crop_center.$1') : '';
        var variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
        var isFree = item.final_line_price === 0;
        var giftAssist = $('[data-gift-assist]', drawer);
        var legacyGiftHandle = giftAssist ? (giftAssist.dataset.giftHandle || '') : '';
        var isLegacyGift = legacyGiftHandle && item.handle === legacyGiftHandle && item.original_price === 0;
        /* Initial render: use original_line_price fallback (compare_at comes later via enrichment) */
        var compareLine = null;
        if (item.original_line_price > item.final_line_price) {
          compareLine = item.original_line_price;
        }

        return '<div class="lp-cart-item" data-key="' + item.key + '" data-variant-id="' + item.variant_id + '" data-handle="' + item.handle + '" data-qty="' + item.quantity + '">' +
          (imgSrc ? '<a href="' + item.url + '" class="lp-cart-item__img"><img src="' + imgSrc + '" alt="' + item.title + '" loading="lazy" width="100" height="100"></a>' : '') +
          '<div class="lp-cart-item__info">' +
            '<a href="' + item.url + '" class="lp-cart-item__title">' + displayProductTitle(item.handle, item.product_title) + '</a>' +
            (variantTitle ? '<span class="lp-cart-item__variant">' + variantTitle + '</span>' : '') +
            '<div class="lp-cart-item__price-row">' +
              (compareLine ? '<s class="lp-cart-item__compare">' + formatMoney(compareLine) + '</s>' : '') +
              (isFree
                ? '<span class="lp-cart-item__free">' + freeLabelText + '</span>'
                : '<span class="lp-cart-item__price">' + formatMoney(item.final_line_price) + '</span>'
              ) +
            '</div>' +
            (isLegacyGift
              ? '<div class="lp-cart-item__qty lp-cart-item__qty--locked"><span class="lp-cart-item__qty-locked">' + lockedGiftText + '</span></div>'
              : '<div class="lp-cart-item__qty">' +
                  '<button type="button" class="lp-cart-item__qty-btn" data-qty-minus aria-label="הפחת">−</button>' +
                  '<span class="lp-cart-item__qty-num">' + item.quantity + '</span>' +
                  '<button type="button" class="lp-cart-item__qty-btn" data-qty-plus aria-label="הוסף">+</button>' +
                '</div>'
            ) +
          '</div>' +
          '<button type="button" class="lp-cart-item__remove" data-remove aria-label="הסר">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
        '</div>';
      }).join('');

      /* Enrich: fetch compare_at_price from product API (cart.json doesn't include it) */
      enrichCompareAtPrices(itemsWrap);
    }

    /* Progress bar */
    updateProgressBar(cart, drawer);
    updateGiftAssist(cart, drawer);
  }

  /* ── Progress bar ── */
  function updateProgressBar(cart, drawer) {
    var bar = $(SELECTORS.progressBar, drawer);
    if (!bar) return;
    var threshold = parseInt(bar.dataset.threshold || '0', 10) * 100; // NIS to cents
    if (threshold <= 0) return;

    var fill = $(SELECTORS.progressFill, drawer);
    var msg = $(SELECTORS.progressMsg, drawer);
    var pct = Math.min(100, (cart.total_price / threshold) * 100);
    if (fill) fill.style.width = pct + '%';

    if (msg) {
      var reached = cart.total_price >= threshold;
      var awayText = bar.dataset.awayText || 'הוסיפו ₪[amount] למשלוח חינם!';
      var earnedText = bar.dataset.earnedText || '🎉 משלוח חינם!';
      if (reached) {
        msg.textContent = earnedText;
        bar.classList.add('lp-progress--reached');
      } else {
        var remaining = formatMoney(threshold - cart.total_price);
        msg.textContent = awayText.replace('[amount]', remaining);
        bar.classList.remove('lp-progress--reached');
      }
    }
  }

  function getQtyByHandle(cart, handle) {
    if (!handle || !cart || !cart.items) return 0;
    return cart.items.reduce(function(total, item) {
      return item.handle === handle ? total + item.quantity : total;
    }, 0);
  }

  function updateGiftAssist(cart, drawer) {
    var assist = $('[data-gift-assist]', drawer);
    if (!assist) return;

    var qualifierHandle = assist.dataset.qualifierHandle || '';
    var giftHandle = assist.dataset.giftHandle || '';
    var qualifyingQty = getQtyByHandle(cart, qualifierHandle);
    var giftQty = getQtyByHandle(cart, giftHandle);
    var missingQty = Math.max(0, qualifyingQty - giftQty);
    var countEl = $('[data-gift-count]', assist);
    var helperEl = $('.lp-cart-gift-assist__helper', assist);
    var bodyEl = $('.lp-cart-gift-assist__body', assist);

    if (!qualifyingQty || !missingQty) {
      assist.hidden = true;
      return;
    }

    assist.hidden = false;
    if (countEl) countEl.textContent = String(missingQty);
    if (bodyEl) {
      bodyEl.textContent = missingQty > 1
        ? 'יש לכם ' + missingQty + ' רצועות שמגיעות עם הפאוץ׳. בחרו צבע והוסיפו אותן לסל.'
        : assist.dataset.bodyTemplate || bodyEl.textContent;
    }
    if (helperEl) {
      helperEl.textContent = missingQty > 1
        ? 'אפשר להוסיף כל רצועה בלחיצה. כמות מעבר למספר הפאוצ׳ים תחויב במחיר הרגיל.'
        : assist.dataset.helperTemplate || helperEl.textContent;
    }
  }

  /* ── Enrich cart items with compare_at_price from product API ──
     cart.json doesn't include variant.compare_at_price, so we fetch
     each product once and inject strikethrough prices into the DOM. */
  var _productCache = {};
  function enrichCompareAtPrices(itemsWrap) {
    var items = itemsWrap.querySelectorAll('.lp-cart-item[data-handle]');
    /* Collect unique handles */
    var handles = {};
    items.forEach(function(el) { handles[el.dataset.handle] = true; });

    var fetches = Object.keys(handles).map(function(handle) {
      if (_productCache[handle]) return Promise.resolve(_productCache[handle]);
      return fetch('/products/' + handle + '.json')
        .then(function(r) { return r.json(); })
        .then(function(data) { _productCache[handle] = data; return data; })
        .catch(function() { return null; });
    });

    Promise.all(fetches).then(function() {
      items.forEach(function(el) {
        var handle = el.dataset.handle;
        var variantId = el.dataset.variantId;
        var qty = parseInt(el.dataset.qty || '1', 10);
        var data = _productCache[handle];
        if (!data || !data.product) return;

        var variant = data.product.variants.find(function(v) { return String(v.id) === String(variantId); });
        if (!variant) return;

        var compareAt = parseFloat(variant.compare_at_price);
        var price = parseFloat(variant.price);
        if (!compareAt || compareAt <= price) return;

        /* Already has a compare line? Skip */
        if (el.querySelector('.lp-cart-item__compare')) return;

        /* Insert strikethrough before the price/free label */
        var priceRow = el.querySelector('.lp-cart-item__price-row');
        if (!priceRow) return;
        var compareEl = document.createElement('s');
        compareEl.className = 'lp-cart-item__compare';
        compareEl.textContent = formatMoney(compareAt * 100 * qty);
        priceRow.insertBefore(compareEl, priceRow.firstChild);
      });
    });
  }

  /* ── Event delegation for qty/remove buttons ── */
  document.addEventListener('click', function(e) {
    var minusBtn = e.target.closest('[data-qty-minus]');
    var plusBtn = e.target.closest('[data-qty-plus]');
    var removeBtn = e.target.closest('[data-remove]');
    var closeBtn = e.target.closest(SELECTORS.close);
    var backdrop = e.target.closest(SELECTORS.backdrop);

    if (closeBtn || backdrop) { close(); return; }

    var item = (minusBtn || plusBtn || removeBtn) ? (minusBtn || plusBtn || removeBtn).closest('.lp-cart-item') : null;
    if (!item) return;
    var key = item.dataset.key;
    if (!key) return;

    var qtyEl = item.querySelector('.lp-cart-item__qty-num');
    var currentQty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;

    if (removeBtn) { changeItem(key, 0); }
    else if (minusBtn) { changeItem(key, Math.max(0, currentQty - 1)); }
    else if (plusBtn) { changeItem(key, currentQty + 1); }
  });

  /* ── Upsell add in drawer ── */
  document.addEventListener('click', function(e) {
    var upsellBtn = e.target.closest('[data-drawer-upsell-add]');
    if (!upsellBtn) return;
    var vid = upsellBtn.dataset.drawerUpsellAdd;
    if (vid) {
      upsellBtn.disabled = true;
      upsellBtn.textContent = 'מוסיף...';
      addItem(vid).then(function() {
        upsellBtn.textContent = 'נוסף ✓';
        setTimeout(function() {
          upsellBtn.textContent = upsellBtn.dataset.originalText || 'הוסף';
          upsellBtn.disabled = false;
        }, 1500);
      });
    }
  });

  document.addEventListener('click', function(e) {
    var giftBtn = e.target.closest('[data-gift-add]');
    if (!giftBtn) return;
    var variantId = giftBtn.dataset.giftAdd;
    if (!variantId) return;
    giftBtn.disabled = true;
    addItem(variantId, 1).then(function() {
      giftBtn.disabled = false;
    }).catch(function() {
      giftBtn.disabled = false;
    });
  });

  /* ── Global event listeners ── */
  document.addEventListener('cart:open', open);
  document.addEventListener('cart:item-added', function() { open(); fetchAndRender(); });
  document.addEventListener('cart:updated', function(e) {
    if (!e.detail || typeof e.detail.item_count !== 'number') return;
    cartData = e.detail;
    updateHeaderCartState(cartData.item_count);
    render(cartData);
  });
  document.addEventListener('keydown', function(e) { if (e.key === 'Escape' && isOpen) close(); });

  /* ── Auto-update header count + progress bar on page load ── */
  document.addEventListener('DOMContentLoaded', function() {
    fetchCart().then(function(cart) {
      if (!cart) return;
      updateHeaderCartState(cart.item_count);
      /* Always update progress bar on load so it shows correct state,
         even when drawer is pre-opened in Customizer design mode */
      var drawerEl = $(SELECTORS.drawer);
      if (drawerEl) updateProgressBar(cart, drawerEl);
    });

    /* ── Shopify theme editor integration ──
       When user selects the cart-drawer section or one of its blocks
       in the theme editor, auto-open the drawer so they can see live
       changes. Also respect the "keep_open" setting. */
    var drawer = $(SELECTORS.drawer);
    if (!drawer) return;

    var keepOpen = drawer.dataset.customizerKeepOpen === 'true';
    if (window.Shopify && Shopify.designMode) {
      if (keepOpen) {
        /* Force open immediately in design mode if toggle is on */
        open();
      }

      /* Shopify editor events */
      document.addEventListener('shopify:section:select', function(e) {
        if (e.target && e.target.querySelector && e.target.querySelector('.lp-cart-drawer')) {
          open();
        }
      });
      document.addEventListener('shopify:section:deselect', function(e) {
        if (!keepOpen && e.target && e.target.querySelector && e.target.querySelector('.lp-cart-drawer')) {
          close();
        }
      });
      document.addEventListener('shopify:block:select', function(e) {
        /* When user selects any block INSIDE the cart drawer */
        if (e.target && e.target.closest && e.target.closest('.lp-cart-drawer')) {
          open();
        }
      });
      document.addEventListener('shopify:block:deselect', function(e) {
        if (!keepOpen && e.target && e.target.closest && e.target.closest('.lp-cart-drawer')) {
          /* Give a brief delay so rapid navigation doesn't flicker */
          setTimeout(function() {
            var anySelected = document.querySelector('.lp-cart-drawer .shopify-block-editor-selected');
            if (!anySelected) close();
          }, 150);
        }
      });
    }
  });
})();
